import { Duration } from 'luxon';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import CopyableField from 'dashboard/components/CopyableField';
import FormResumeInquiry, { ResumeInquiryFormData } from 'dashboard/forms/FormResumeInquiry';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Environment } from 'dashboard/models/environment';
import { COLORS } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { getRootDomain } from 'lib/utils/url';

const ButtonContainer = styled('div')`
  display: flex;
`;

const StyledCopyableField = styled(CopyableField)`
  padding-bottom: ${SPACINGS.base * 5.5}px;
`;

const StyledButtonClose = styled(Button)`
  margin-right: ${SPACINGS.base}px;
`;

const StyleWrapper = styled('a')`
  white-space: nowrap;

  input {
    color: ${COLORS.indigo80};
    cursor: pointer;
  }
`;

const StyledBody = styled('p')`
  margin-bottom: ${SPACINGS.base}px;
`;

interface ResumeInquiryModalProps {
  expirySeconds?: number;
  resumeCode: string;
  emailAddress: string;
  subdomain: string;
  customDomain: string;
  currentEnvironment: Environment;
  handleClose: () => void;
  handleResumeInquiry: (values: ResumeInquiryFormData) => void;
}

const ResumeInquiryModal = ({
  expirySeconds,
  resumeCode,
  emailAddress,
  // TODO(neal): set the default value for subdomain to inquiry after subdomain rolled out
  subdomain,
  customDomain,
  currentEnvironment,
  handleClose,
  handleResumeInquiry,
}: ResumeInquiryModalProps) => {
  let link;
  if (resumeCode) {
    if (customDomain) {
      link = `${location.protocol}//${customDomain}/verify?code=${resumeCode}`;
    } else {
      link = `${location.protocol}//${[subdomain, getRootDomain(location.host)]
        .filter(Boolean)
        .join('.')}/verify?code=${resumeCode}`;
    }
  }
  return (
    <ModalContainer data-test='resume-inquiry-modal'>
      <ModalHeader data-test='resume-inquiry-modal__header' onClose={handleClose}>
        {resumeCode ? 'Inquiry resumed' : 'Resume this inquiry?'}
      </ModalHeader>
      <ModalBody>
        {resumeCode && (
          <>
            <StyledBody>
              This one-time link is active for{' '}
              {Duration.fromObject({ seconds: expirySeconds ?? 86400 })
                .rescale()
                .toHuman()}
              . <strong> Please note:</strong> This link is not recoverable once this window is
              closed.
            </StyledBody>
            {link != null && (
              <StyleWrapper href={link} target='_blank'>
                <StyledCopyableField
                  id='resume-inquiry-modal__inquiry-link'
                  data-test='resume-inquiry-modal__inquiry-link'
                  value={link}
                />
              </StyleWrapper>
            )}
            <ButtonContainer>
              <StyledButtonClose
                id='resume-inquiry-modal__close-button'
                data-test='resume-inquiry-modal__close-button'
                onClick={handleClose}
              >
                Close
              </StyledButtonClose>
            </ButtonContainer>
          </>
        )}

        {!resumeCode && (
          <FormResumeInquiry
            onSubmit={handleResumeInquiry}
            initialValues={{ emailAddress: emailAddress }}
            currentEnvironment={currentEnvironment}
          />
        )}
      </ModalBody>
    </ModalContainer>
  );
};

export default ResumeInquiryModal;
